import React, { useState, useEffect, useRef } from 'react';
import { MINDY_API_URL } from '../../config/mindyApi.js';
import { DEMO_AUTH_MODE, readDemoSession } from '../../config/demoAuth';
import { useRecoilState } from 'recoil';
import { userIDAtom, chatMessagesAtom, sessionIDAtom } from '../../store/atoms';
import {
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { useIsMobile } from '../../customHook/useIsMobile';

const Chat = () => {
  const [messages, setMessages] = useRecoilState(chatMessagesAtom);
  console.log("Chat Messages from Atom:", messages); // Debugging log
  const [userID, setUserID] = useRecoilState(userIDAtom);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const messagesEndRef = useRef(null);
  const [sessionID, setSessionID] = useRecoilState(sessionIDAtom);
  const [cognitoEmail, setCognitoEmail] = useState('');
  const [mindyColorChange, setMindyColorChange] = useState(false);
  const[isParent, setIsParent] = useState(false);
 const handleMindyColorChange = () => {
   setMindyColorChange(true); 
 };

  useEffect(() => {
    const applyDemoSession = () => {
      const session = readDemoSession();
      if (!session) {
        return;
      }
      const attributes = session.attributes || {};
      const isParentFlag =
        String(attributes['custom:parentProfile'] || '').toLowerCase() === 'true';
      setIsParent(isParentFlag);
      setUserID(session.attributes?.email || session.username);
      if (attributes.email) {
        setCognitoEmail(attributes.email);
      }

      const givenName =
        attributes['custom:given_name'] ||
        attributes.given_name ||
        'Friend';

      const parentWelcomeMessage = {
        text: {
          reply: `Hey <strong>${givenName}</strong>, hope you're having a banner day! 😊 How can I help?`,
          contains_html: true,
        },
        type: 'mindy',
      };

      const studentWelcomeMessage = {
        text: {
          reply: `Hi <strong>${givenName}</strong>. Nelson Mandela said, "It always seems impossible until it's done." So what do you want to make possible today? 🚀`,
          contains_html: true,
        },
        type: 'mindy',
      };

      setMessages((prevMessages) => {
        if (prevMessages.length === 0) {
          return [isParentFlag ? parentWelcomeMessage : studentWelcomeMessage];
        }
        return prevMessages;
      });
    };

    const getUserAttributes = async () => {
      if (DEMO_AUTH_MODE) {
        applyDemoSession();
        return;
      }
      try {
        const attributes = await fetchUserAttributes();

        if (attributes) {
          if (attributes.email) {
            setCognitoEmail(attributes.email);
            setUserID(attributes.email);
          }

          const isParentFlag =
            attributes['custom:parentProfile'] &&
            attributes['custom:parentProfile'].toLowerCase() === 'true';

          setIsParent(isParentFlag);

          const givenName =
            attributes['custom:given_name'] ||
            attributes.given_name ||
            attributes.name ||
            'Friend';

          const studentName =
            attributes['custom:name'] ||
            attributes.name ||
            '';

          if (isParentFlag && studentName) {
            localStorage.setItem('studentName', studentName);
            console.log(' Stored student name from Cognito:', studentName);
          }

          const parentWelcomeMessage = {
            text: {
              reply: `Hey <strong>${givenName}</strong>, hope you're having a banner day! 😊 How can I help?`,
              contains_html: true,
            },
            type: 'mindy',
          };

          const studentWelcomeMessage = {
            text: {
              reply: `Hi <strong>${givenName}</strong>. Nelson Mandela said, "It always seems impossible until it's done." So what do you want to make possible today? 🚀`,
              contains_html: true,
            },
            type: 'mindy',
          };

          setMessages((prevMessages) => {
            if (prevMessages.length === 0) {
              return [isParentFlag ? parentWelcomeMessage : studentWelcomeMessage];
            }
            return prevMessages;
          });
        }
      } catch (error) {
        console.error('Error fetching user attributes:', error);
      }
    };

    getUserAttributes();
}, []);


  /* Add this helper component */
  const SimulationMessages = ({ simulation }) => {
    const [visibleMessages, setVisibleMessages] = React.useState([]);

    React.useEffect(() => {
      let timers = [];

      simulation.forEach((item, index) => {
        const timer = setTimeout(() => {
          setVisibleMessages((prev) => [...prev, item]);
        }, item.delay);
        
        timers.push(timer);
      });

      return () => timers.forEach(clearTimeout);
    }, [simulation]);

    return (
      <div style={{ 
        fontSize: "14px", 
        lineHeight: "1.6",
        background: 'rgba(255, 255, 255, 0.12)',
        borderRadius: '20px',
        padding: '16px',
        color: '#FFFFFF',
        fontFamily: 'Manrope',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(15px)',
        marginBottom: '20px' // Added margin to create gap below the simulation bubble
      }}>
        {visibleMessages.map((item, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: idx < visibleMessages.length - 1 ? "14px" : "0",
              color: "#FFFFFF",
              fontFamily: "Manrope",
              fontSize: "15px",
              fontWeight: 500,
              letterSpacing: "-0.3px",
              opacity: 0,
              animation: "fadeIn 0.6s forwards",
              animationDelay: `${idx * 0.1}s`,
              textAlign: 'center'
            }}
          >
            {item.image ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <img 
                  src={`/${item.image}`} 
                  alt="Delivery"
                  style={{
                    maxWidth: '100%', // Responsive width
                    maxHeight: '300px', // Optional: Set a max height if needed
                    height: 'auto', // Maintain aspect ratio
                    marginBottom: '8px',
                    objectFit: 'contain' // Ensures image maintains aspect ratio without cropping
                  }}
                  onLoad={(e) => {
                    // Optional: Log image dimensions for debugging
                    console.log(`Image loaded: ${item.image}`, {
                      naturalWidth: e.target.naturalWidth,
                      naturalHeight: e.target.naturalHeight,
                      displayedWidth: e.target.width,
                      displayedHeight: e.target.height
                    });
                  }}
                />
              </div>
            ) : (
              <div style={{
                padding: '8px 0'
              }}>
                {item.message}
              </div>
            )}
          </div>
        ))}

        <style>
          {`
            @keyframes fadeIn {
              0% { opacity: 0; transform: translateY(6px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
      </div>
    );
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!sessionID) {
      const newSessionID = Math.random().toString(36).substring(2, 15) + 
                           Math.random().toString(36).substring(2, 15);
      setSessionID(newSessionID);
      console.log("New session ID created:", newSessionID);
    }
  }, [sessionID]);

  const sendMessage = async (messageText) => {
  if (!messageText.trim() || isLoading) return;

  // Add user message to chat
  const userMessage = {
    type: 'user',
    text: messageText,
    timestamp: new Date()
  };
  setMessages(prev => [...prev, userMessage]);
  setInputValue('');
  setIsLoading(true);

  try {
    const demo = DEMO_AUTH_MODE ? readDemoSession() : null;
    const parentFlag = demo
      ? String(demo.attributes?.['custom:parentProfile'] || '').toLowerCase() === 'true'
      : Boolean(isParent);
    const effectiveUserId = demo?.username || userID;
    /* Parent rows are keyed by email/name from parent_profiles_v2; students need student_profiles.email. */
    const apiUserId = parentFlag
      ? effectiveUserId
      : (cognitoEmail ||
          demo?.attributes?.email ||
          demo?.username ||
          userID ||
          '');

    const studentName = parentFlag ? (localStorage.getItem('studentName') || '') : '';

    const reqBody = JSON.stringify({
      user_id: apiUserId,
      message: messageText,
      session_id: sessionID,
      user_type: parentFlag ? 'parent' : 'student',
      student_name: studentName,
      is_parent: parentFlag,
    });

    console.log('Request Body:', reqBody);
    const response = await fetch(MINDY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: reqBody
      });

      const rawText = await response.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error(rawText.slice(0, 200) || 'Non-JSON response from server');
      }
      console.log("Full response JSON:", data);

      const inner = data.body;
      let responseBody;
      if (typeof inner === 'string') {
        try {
          responseBody = JSON.parse(inner);
        } catch {
          responseBody = { reply: inner };
        }
      } else {
        responseBody = inner ?? data;
      }
      console.log("Parsed response body:", responseBody);

      if (!response.ok) {
        const mindyError = {
          type: 'mindy',
          text:
            responseBody && (responseBody.reply || responseBody.error)
              ? responseBody
              : {
                  reply:
                    typeof responseBody === 'string'
                      ? responseBody
                      : `Request failed (${response.status})`,
                },
          chips: [],
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, mindyError]);
        return;
      }

      if (responseBody.parent_name) {
        setUserName(responseBody.parent_name);
      }

      const mindyMessage = {
        type: 'mindy',
        text: responseBody,
        chips: responseBody.show_chips && responseBody.chips ? responseBody.chips : [],
        timestamp: new Date()
      };
      console.log('Mindy Message:', mindyMessage);
      setMessages(prev => [...prev, mindyMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        type: 'mindy',
        text: 'Sorry, I encountered an error. Please try again.',
        chips: [],
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendClick = () => {
    sendMessage(inputValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleChipClick = (chip) => {
  // Handle both string chips (backward compatibility) and object chips
  const chipText = typeof chip === 'object' ? chip.text : chip;
  sendMessage(chipText);
};

  //responsive design for mobile
  const mobileWidth = useIsMobile(); 

  return (
  <>
    {/* Chat Messages Section */}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      padding: '20px 60px 120px',
      maxWidth: '700px',
      margin: '0 auto'
    }}>
      {messages 
      .filter((message) => message.text !== '911') 
      .map((message, index) => (
        <div key={index}>
          {message.type === 'user' ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  color: '#FFFFFF',
                  fontFamily: 'Manrope',
                  fontSize: '13px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  paddingRight: '8px'
                }}>
                  {/* {userName} */}
                </div>

                <div style={{
                  display: 'inline-block',
                  padding: '8px 12px',
                  borderTopLeftRadius: '20px',
                  borderTopRightRadius: '24px',
                  borderBottomRightRadius: '20px',
                  borderBottomLeftRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  textAlign: 'left'
                }}>
                  <span style={{
                    color: '#FFFFFF',
                    fontFamily: 'Manrope',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '120%',
                    letterSpacing: '-0.3px'
                  }}>
                    {message.text}
                  </span>
                </div>
              </div>

              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#E0E0E0',
                  backgroundImage: isParent
                    ? 'url("/parent.png")'  // If parentProfile === true
                    : 'url("/student.png")',  // If parentProfile === false
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                }}
              />
            </div>
          ) : (
            // Mindy Message
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#E0E0E0',
                backgroundImage: 'url("/mindychat2.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0
              }} />

              <div>
                <div style={{
                  color: '#FFFFFF',
                  fontFamily: 'Manrope',
                  fontSize: '13px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  paddingLeft: '8px'
                }}>
                  {/* Mindy */}
                </div>

                <div style={{
                  padding: '8px 12px',
                  borderTopLeftRadius: '24px',
                  borderTopRightRadius: '20px',
                  borderBottomRightRadius: '20px',
                  borderBottomLeftRadius: '20px',
                  WebkitBackdropFilter: 'blur(20px)',
                  marginBottom: message.chips?.length > 0 ? '12px' : '0',
                  textAlign: 'left'
                }}>
                  <span style={{
                    color: '#FFFFFF',
                    fontFamily: 'Manrope',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '120%',
                    letterSpacing: '-0.3px'
                  }}>
                    {typeof message.text === 'object' 
                      ? (message.text.contains_html 
                          ? <span dangerouslySetInnerHTML={{ __html: message.text.reply }} />
                          : message.text.reply || JSON.stringify(message.text))
                      : message.text}
                  </span>
                </div>

                {/*  Order Card Rendering */}
                {message.text && message.text.order_card && (
                  <div
                    style={{
                      background: 'rgba(255, 255, 255, 0.12)',
                      borderRadius: '20px',
                      padding: '16px',
                      color: '#FFFFFF',
                      fontFamily: 'Manrope',
                      marginTop: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(15px)',
                    }}
                  >
                    {/* Determine the order card type dynamically */}
                    {'type' in message.text.order_card ? (
                      // ===== Type 2: Crisis Request Card =====
                      <>
                        <h3 style={{ fontSize: '17px', fontWeight: 600, marginBottom: '10px' }}>
                          {message.text.order_card.type}
                        </h3>
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <p><strong>Submitted by:</strong> {message.text.order_card.submitted_by}</p>
                          <p><strong>Student:</strong> {message.text.order_card.student}</p>
                          <p><strong>University:</strong> {message.text.order_card.university}</p>
                          <p><strong>Dorm:</strong> {message.text.order_card.dorm}</p>
                          <p><strong>Description:</strong> {message.text.order_card.description}</p>
                          <p><strong>Preferred Contact:</strong> {message.text.order_card.preferred_contact}</p>
                          <p><strong>Urgency:</strong> {message.text.order_card.urgency}</p>
                        </div>
                      </>
                    ) : (
                      // ===== Type 1: Delivery Order Card =====
                      <>
                        <h3 style={{ fontSize: '17px', fontWeight: 600, marginBottom: '10px' }}>
                          {message.text.order_card.package}
                        </h3>
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          {/* <p><strong>Intent:</strong> {message.text.order_card.main_intent}</p>
                          <p><strong>Category:</strong> {message.text.order_card.subcategory}</p> */}
                          <p><strong>Recipient:</strong> {message.text.order_card.recipient}</p>
                          <p><strong>University:</strong> {message.text.order_card.recipient_university}</p>
                          <p><strong>Delivery Timing:</strong> {message.text.order_card.delivery_timing}</p>
                          <p><strong>Delivery Window:</strong> {message.text.order_card.delivery_window}</p>
                          {message.text.order_card.personalized_message && (
                            <p><strong>Message:</strong> {message.text.order_card.personalized_message}</p>
                          )}
                          <p><strong>Price:</strong> {message.text.order_card.price}</p>
                          {/* <p><strong>Occasion:</strong> {message.text.order_card.occasion}</p> */}
                        </div>
                      </>
                    )}

                    {/* ===== Confirm Buttons (if present) ===== */}
                    {message.text.confirm_buttons && (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                        {message.text.confirm_buttons.map((btn, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleChipClick(btn)}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '21px',
                              background:
                                idx === 0
                                  ? 'rgba(255, 255, 255, 0.40)'
                                  : 'transparent',
                              border:
                                idx === 0
                                  ? '1px solid rgba(255, 255, 255, 0.5)'
                                  : '1px solid #FFFFFF',
                              color: '#FFFFFF',
                              backdropFilter: idx === 0 ? 'blur(10px)' : 'none',
                              cursor: 'pointer',
                              fontWeight: 500,
                            }}
                          >
                            {btn}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Delivery Simulation Messages */}
                {message.text && message.text.delivery_simulation && (
                  <SimulationMessages simulation={message.text.delivery_simulation} />
                )}

                {message.chips?.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {message.chips.map((chip, chipIndex) => (
                      <button
                        key={chipIndex}
                        onClick={() => handleChipClick(chip)}
                        disabled={isLoading}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '17px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          background: 'rgba(255, 255, 255, 0.15)',
                          backdropFilter: 'blur(10px)',
                          color: '#FFFFFF',
                          fontFamily: 'Manrope',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: isLoading ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {/* Render image if chip is an object with image property */}
                        {typeof chip === 'object' && chip.image && (
                          <img 
                            src={chip.image} 
                            alt=""
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        {typeof chip === 'object' ? chip.text : chip}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#E0E0E0',
            backgroundImage: 'url("/mindychat2.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0
          }} />
          <div style={{
            padding: '8px 12px',
            color: '#FFFFFF',
            fontFamily: 'Manrope',
            fontSize: '15px'
          }}>
            Typing...
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>

    {/* Fixed Input Bar at Bottom */}
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: !mobileWidth ?'50%' : '59%',
      transform: 'translateX(-60%)',
      width: !mobileWidth ?'582px' : '359px',
      height: !mobileWidth ? '60px' : '55px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '8px 16px 8px 8px',
      borderRadius: '36px',
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid #ffffff',
      zIndex: 100
    }}>
      {/* Crisis Button */}
      <div 
        onClick={() => {
        handleMindyColorChange();
        sendMessage('mindyNOW');
      }}
        style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: !mobileWidth ?'8px 16px' : '2px 10px',
        borderRadius: '21px',
        background: mindyColorChange
          ? "#FFF"
          : "rgba(255, 255, 255, 0.40)", //  color logic
        cursor: 'pointer',
        height: !mobileWidth ? '44px' : '40px'
      }}>
        <img
          src="/History.svg"
          alt="Crisis"
          style={{ width: !mobileWidth ? '20px' : '15px', height: !mobileWidth ? '20px' : '15px' }}
        />

        <span style={{
          fontFamily: 'Manrope',
          fontWeight: 600,
          fontSize: ! mobileWidth ?'18px' : '13px',
          lineHeight: '28px',
          letterSpacing: '0.5%',
          background: 'rgba(255, 0, 0, 0.6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          mindyNOW
        </span>
      </div>

      {/* Input Field */}
      <>
        <input
          type="text"
          placeholder="Start typing..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={isLoading}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#FFFFFF',
            fontFamily: 'Manrope',
            fontSize: !mobileWidth ? '18px' : '13px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '28px',
            letterSpacing: '0.09px',
          }}
        />

        <style>
          {`
            input::placeholder {
              color: rgba(255, 255, 255, 0.40);
              font-family: Manrope;
              font-size: 18px;
              font-style: normal;
              font-weight: 600;
              line-height: 28px;
              letter-spacing: 0.09px;
            }
          `}
        </style>
      </>



      {/* Send Button */}
      <button
        onClick={handleSendClick}
        disabled={isLoading || !inputValue.trim()}
        style={{
          width: !mobileWidth ? '40px' : '30px',
          height: !mobileWidth ? '40px' : '30px',
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          cursor: (isLoading || !inputValue.trim()) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
          opacity: (isLoading || !inputValue.trim()) ? 0.5 : 1
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M4.49746 20.835L21.0072 13.4725C22.3309 12.8822 22.3309 11.1178 21.0072 10.5275L4.49746 3.16496C3.00163 2.49789 1.45006 3.97914 2.19099 5.36689L5.34302 11.2706C5.58817 11.7298 5.58818 12.2702 5.34302 12.7294L2.19099 18.6331C1.45007 20.0209 3.00163 21.5021 4.49746 20.835Z" fill="white" fill-opacity="0.8"/>
        </svg>
      </button>
    </div>
  </>
);
};
export default Chat;
